import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import queryString from "query-string";

import PageTitle from "../../components/page-title";
import SegmentsTopbar from "./segments-topbar";
import Loader from "../../components/loader";
import NoData from "../../components/no-data";
import FormSelect from "../../components/form-select";
import NewCustomSegmentModal from "../../components/modal/new-custom-segment-modal";
import GuidedTour from "../../components/guided-tour";

import { useGuidedTour } from "../../hooks/useGuidedTour";

import {
  getSegmentsAction,
  postAddSegmentToDbAction,
} from "../../redux/dashboard/segments-slice";

import { hexCodeGenerate, tourGuideCheck } from "../../utils/functions";
import {
  APP_LIST_LAYOUT_GRID,
  APP_LIST_LAYOUT_LIST,
} from "../../utils/constants";
import SegmentInfoPage from "./segment-info";
import { CallBackProps } from "react-joyride";

// import ComingSoon from "../../components/coming-soon";

const listOrderOptions = [
  { label: "Most Recent", value: "desc" },
  { label: "Oldest", value: "asc" },
];

const SegmentsPage = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();

  const isFirstRender = useRef(true);

  const { listLayout } = useSelector((state: any) => state.componentsSlice);
  const { isFetchingSegments, segments } = useSelector(
    (state: any) => state.segmentsSlice
  );

  const {segmentType, segmentId} = queryString.parse(location.search);

  const [param, setParam] = useState( segmentType ?? "ai");
  
  const [listOrder, setListOrder] = useState(listOrderOptions[0]);
  // const [segmentId, setSegmentId] = useState("");
  const [showSegmentDetails, setShowSegmentDetails] = useState(false);
  const [isNewCustomSegmentModalOpen, setIsNewCustomSegmentModalOpen] =
    useState(false);
  const [isGeneratingSegment, setIsGeneratingSegment] =
    useState<boolean>(false);
  const [generatedData, setGeneratedData] = useState<any>({});

  const {
    runTour,
    setRunTour,
    tourStepIndex,
    setTourStepIndex,
    isSecondTour,
    setIsSecondTour,
  } = useGuidedTour();


 
    const isExplicitlySearching = location.search.includes("segmentType");
  
    const updateUrlWithSegment = (id: string | null) => {
      if (id) {
        history.push({
          pathname: "/segments",
          search: `?segmentType=${param}&segmentId=${id}`,
        });
      } else {
        history.push({
          pathname: "/segments",
          search: `?segmentType=${param}`,
        });
      }
    };




  const createSegmentTourSteps = [
    {
      target: "#new-segment-modal",
      content: generatedData?.segment_description
        ? "You can rename a segment as desired and save using the button above."
        : "Enter a prompt to create a custom segment, or get AI-generated segments with 'Ask AI'",
      disableBeacon: true,
      placement: "top",
    },
  ];

  const getSegments = (value: string) => {
    // setParam(value);
   dispatch(getSegmentsAction({ type: value }));
  };

  useEffect(() => {
      if (isFirstRender.current) {
      // Skip this effect on first render
      isFirstRender.current = false;
      return;
    }
    updateUrlWithSegment(null);
  }, [param])
  

  useEffect(() => {
  if (segmentType && typeof segmentType === "string") {
        getSegments(segmentType);
    }
   
  }, [segmentType])
  

  useEffect(() => {
    // getSegments(param);
    
    tourGuideCheck("segmentTourCompleted", setRunTour);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNewCustomSegmentModalOpen && isSecondTour) {
      tourGuideCheck("createSegmentTourCompleted", setRunTour);
    }
    if (!isNewCustomSegmentModalOpen && isSecondTour) {
      // localStorage.setItem("createSegmentTourCompleted", "true");
      setRunTour(false);
    }
  }, [isNewCustomSegmentModalOpen]);

  useEffect(() => {
    console.log();
    if (isSecondTour) {
      if (!generatedData?.segment_description || isGeneratingSegment) {
        // setTourStepIndex(1);
        setRunTour(false);
      } else if (generatedData?.segment_description) {
        setRunTour(true);
      }
    }
  }, [generatedData, isGeneratingSegment]);

  // useEffect(() => {
  //   if (tourStepIndex === 2 && isNewCustomSegmentModalOpen) {
  //     // Now that the target component should be rendered, advance to step 3.
  //     setTourStepIndex(3);
  //   }
  // }, [isNewCustomSegmentModalOpen]);

  const postAddSegmentToDb = (payload: any) => {
    dispatch(postAddSegmentToDbAction(payload)).then((res: any) => {
      if (res?.success === true) {
        if (param && typeof param === 'string')getSegments(param);
        updateUrlWithSegment(res?.segmentId);
        
        setIsNewCustomSegmentModalOpen(false);
      }
    });
  };

  // const handleSegmentDetails = (id: string) => {
  //   // setSegmentId(id);
  //   updateUrlWithSegment(id);
  //   // setShowSegmentDetails(true);
  // };

  const groupedData = () => {
    // First, map each segment to include the full timestamp
    const campaignsWithDate = segments?.map((segment: any) => {
      const date = new Date(segment.created_at);
      return {
        ...segment,
        timestamp: date.getTime(),
        // Format date without time for grouping
        dateString: date.toLocaleDateString(),
      };
    });

    // First sort by timestamp in descending order (most recent first)
    const sortedSegments = campaignsWithDate.sort(
      (a: any, b: any) => b.timestamp - a.timestamp
    );

    // Group by date
    const grouped = sortedSegments.reduce((acc: any, segment: any) => {
      acc[segment.dateString] = acc[segment.dateString] || [];
      acc[segment.dateString].push(segment);
      return acc;
    }, Object.create(null));

    // Convert to array and ensure each group is properly sorted by time
    return (
      Object.entries(grouped)
        .map(([dateString, campaigns]: any) => ({
          createdAt: dateString,
          // Sort campaigns within each date group by timestamp in descending order
          data: campaigns.sort((a: any, b: any) => b.timestamp - a.timestamp),
        }))
        // Finally, sort the date groups themselves
        .sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        })
    );
  };

  const segmentsData =
    listOrder?.value === listOrderOptions[0].value
      ? groupedData()
      : groupedData().reverse();

  console.log("segmentsData:", segmentsData);

  // const segmentsData =
  //   listOrder?.value === listOrderOptions[0].value
  //     ? segments || []
  //     : segments?.slice()?.reverse() || [];

  const segmentsAvailable = segmentsData?.length > 0;

  const segmentDashboardTourSteps = [
    {
      target: "#AI-Generated",
      content: "Find AI-generated customer segments in this tab.",
      disableBeacon: true,
    },
    {
      target: "#Custom",
      content: "Find the segments I created for you based on your requests.",
      disableBeacon: true,
    },
    {
      target: "#new-segment-btn",
      content: "Click here to create a new segment",
      disableBeacon: true,
    },

    // {
    //   target: "#segment-item",
    //   content: "Click on each customer segment to see more details about it.",
    // },

    // {
    //   target: ".segments_info_page_container",
    //   content:
    //     "Here, you will see the segment summary and the customer list for this segment.",
    // },
  ];

  const segmentTourCallBack = (data: CallBackProps) => {
    const { action, index, status, type } = data;
    console.log(action, index, status, type);

    if (type === "step:after" && action === "prev") {
      setTourStepIndex(index - 1);
    }

    if (type === "step:after" && action === "next") {
      setTourStepIndex(index + 1);
    }
    if (status === "finished" || status === "skipped") {
      setTourStepIndex(0);
      setRunTour(false);

      if (isSecondTour) {
        localStorage.setItem("createSegmentTourCompleted", "true");
        setIsSecondTour(false);
      } else {
        setIsSecondTour(true);
        localStorage.setItem("segmentTourCompleted", "true");
      }
    }
  };

  return (
    <React.Fragment>
      <GuidedTour
        steps={
          isSecondTour ? createSegmentTourSteps : segmentDashboardTourSteps
        }
        styles={{
          options: {
            overlayColor: isSecondTour ? "none" : "rgba(0, 0, 0, 0.5)",
          },
        }}
        isSpotlightClicks={isSecondTour}
        runTour={runTour}
        setRunTour={setRunTour}
        tourStepIndex={tourStepIndex}
        setTourStepIndex={setTourStepIndex}
        disableNextBtn={isSecondTour}
        hideBackBtn={isSecondTour}
        tourCallBack={segmentTourCallBack}
      />
      <PageTitle title="Segments" />

      <SegmentsTopbar
        // setShowSegmentDetails={setShowSegmentDetails}
        generateCustom={() => setIsNewCustomSegmentModalOpen(true)}
        // onFilter={getSegments}
        type={param as string}
        // setType={setParam}
        onFilter={setParam}
        activeFilterIndex={tourStepIndex}
      />

      <div className="page_container segments_page_container">
        {isFetchingSegments && <Loader />}

        {!isFetchingSegments && segmentsAvailable ? (
          <>
            {segmentId ? (
              <SegmentInfoPage
                id={segmentId as string}
                type={param as string}
                getSegments={getSegments}
                // setShowSegmentDetails={setShowSegmentDetails}
                updateUrlWithSegment={updateUrlWithSegment}
              />
            ) : (
              <>
                <div className="top_filters">
                  <FormSelect
                    name="filterSelect"
                    defaultValue={listOrder}
                    options={listOrderOptions}
                    onChange={(value: any) => setListOrder(value)}
                  />
                </div>

                {listLayout === APP_LIST_LAYOUT_LIST && (
                  <div className="vertical_scroll">
                    <div className="layout list_layout">
                      {segmentsData?.map((segment: any, index: number) => (
                        <div className="list_box" key={index}>
                          <p className="date_title">{segment?.createdAt}</p>
                          <div>
                            {segment?.data?.map((item: any, i: any) => (
                              <div
                                key={i}
                                id={"#segment-item"}
                                className="item"
                                // onClick={() => handleSegmentDetails(item.id)}
                                onClick={() =>  updateUrlWithSegment(item.id)}
                              >
                                <div className="sn_and_title">
                                  {/* <p
                                    className="sn"
                                    style={{ color: `${hexCodeGenerate()}` }}
                                  >
                                    {i + 1}
                                  </p> */}

                                  <p className="title">{item.segment_name}</p>
                                </div>

                                <p
                                  className="info"
                                  dangerouslySetInnerHTML={{
                                    __html: item.segment_description,
                                  }}
                                ></p>

                                <p className="customers">
                                  {item?.customer_count} customers
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {listLayout === APP_LIST_LAYOUT_GRID && (
                  <div className="layout grid_layout">
                    {segmentsData?.map((item: any, i: any) => (
                      <div key={i} className="item">
                        <div className="top">
                          <p
                            className="sn"
                            style={{ color: `${hexCodeGenerate()}` }}
                          >
                            {i + 1}
                          </p>
                        </div>
                        <Link to={`/segments/${item.id}`} className="title">
                          {item.segment_name}
                        </Link>

                        <p
                          className="info"
                          dangerouslySetInnerHTML={{
                            __html: item.segment_description,
                          }}
                        ></p>

                        <div className="footer">
                          <p className="customers">
                            {item?.customer_count} customers
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : !isFetchingSegments && segmentsData?.length === 0 ? (
          <NoData text={`No ${param} generated segments`} />
        ) : (
          <></>
        )}
      </div>

      <NewCustomSegmentModal
        isOpen={isNewCustomSegmentModalOpen}
        isGenerating={isGeneratingSegment}
        setIsGenerating={setIsGeneratingSegment}
        generatedData={generatedData}
        setGeneratedData={setGeneratedData}
        type={param as string}
        setType={setParam as React.Dispatch<React.SetStateAction<string>>}
        close={() => setIsNewCustomSegmentModalOpen(false)}
        onSubmit={(payload) => postAddSegmentToDb(payload)}
      />
    </React.Fragment>
  );
};

export default SegmentsPage;
